'use client';

import React from 'react';
import { useLocale, useTranslations } from 'next-intl';
import Icon from '@/components/ui/AppIcon';
import { PROMO } from '@/config/promo';
import { formatMoney } from '@/lib/shopify/format';
import type { Money } from '@/lib/shopify/types';
import { useShop } from './ShopProvider';

/** Shared pieces of the Black Friday + free shipping announcement. All text comes from messages/*.json. */

/** Name of the country the free shipping applies to, in the visitor's language ("España", "Spain"…). */
export function useFreeShippingCountry() {
  const locale = useLocale();
  const code = PROMO.freeShippingCountries[0];
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** "● Oferta especial": a copper dot and one quiet line, no box. (The end date is internal: it is never shown.) */
export function PromoBadge({
  tone = 'dark',
  className = '',
}: {
  tone?: 'dark' | 'paper';
  className?: string;
}) {
  const t = useTranslations('promo');
  return (
    <span
      className={`inline-flex items-center gap-2.5 [font-family:var(--font-body),sans-serif] text-[13.5px] leading-none tracking-[0.01em] ${tone === 'paper' ? 'text-[var(--paper-smoke)]' : 'text-[rgba(242,235,224,0.72)]'} ${className}`}
    >
      <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--copper)] shrink-0" />
      <span>
        <b className="font-semibold text-copper">{t('badge')}</b>
      </span>
    </span>
  );
}

/** "Envío GRATIS a España por tiempo limitado" — only inside the promo window. `tone="paper"` is for the roast-brown home section. */
export function FreeShippingNote({
  short = false,
  tone = 'dark',
  className = '',
}: {
  short?: boolean;
  tone?: 'dark' | 'paper';
  className?: string;
}) {
  const t = useTranslations('promo');
  const country = useFreeShippingCountry();
  const { promo } = useShop();
  if (!promo.freeShipping) return null;
  return (
    <p
      className={`flex items-center gap-2.5 [font-family:var(--font-body),sans-serif] text-[14.5px] leading-snug tracking-[0.01em] font-medium ${tone === 'paper' ? 'text-[var(--paper-ink)]' : 'text-[rgba(242,235,224,0.9)]'} ${className}`}
    >
      <Icon name="TruckIcon" size={16} variant="outline" className="shrink-0 text-copper" />
      <span>{short ? t('freeShippingShortLimited') : t('freeShippingLimited', { country })}</span>
    </p>
  );
}

/**
 * Hero price on the product page: the sale price is the protagonist, the previous price small and quiet,
 * and the saving is just the difference between the two prices the page already shows.
 */
export function PromoPriceBlock({ regular, sale }: { regular: Money; sale: Money }) {
  const t = useTranslations('promo');
  const locale = useLocale();
  const saving = Number(regular.amount) - Number(sale.amount);
  return (
    <div className="promo-in">
      <PromoBadge />
      <p className="display text-[clamp(54px,4.6vw,72px)] leading-none mt-3.5 text-[var(--cream)]">
        <span className="sr-only">{t('now')}: </span>
        {formatMoney(sale, locale)}
      </p>
      <p className="mt-3.5 flex items-baseline gap-3.5 text-[15px] leading-none">
        <s className="text-[var(--ash)] [text-decoration-thickness:1px]">
          <span className="sr-only">{t('was')}: </span>
          {formatMoney(regular, locale)}
        </s>
        {saving > 0.004 && (
          <span className="text-copper text-[14px]">
            {t('save', { amount: formatMoney({ amount: saving.toFixed(2), currencyCode: sale.currencyCode }, locale) })}
          </span>
        )}
      </p>
    </div>
  );
}

/** Previous price struck through + sale price, with accessible names for screen readers. */
export function PromoPrices({
  regular,
  sale,
  tone = 'dark',
  strikeClassName = '',
  className = '',
}: {
  regular: Money;
  sale: Money;
  tone?: 'dark' | 'paper';
  strikeClassName?: string;
  className?: string;
}) {
  const t = useTranslations('promo');
  const locale = useLocale();
  return (
    <span className={`inline-flex items-baseline gap-3 ${className}`}>
      <s className={`${tone === 'paper' ? 'text-[var(--paper-smoke)]' : 'text-[var(--ash)]'} ${strikeClassName}`}>
        <span className="sr-only">{t('was')}: </span>
        {formatMoney(regular, locale)}
      </s>
      <span className={tone === 'paper' ? 'text-[var(--paper-ink)]' : 'text-copper'}>
        <span className="sr-only">{t('now')}: </span>
        {formatMoney(sale, locale)}
      </span>
    </span>
  );
}

/** Thin announcement strip at the top of the header. Renders nothing when there is nothing to announce. */
export function PromoBar({ collapsed }: { collapsed: boolean }) {
  const t = useTranslations('promo');
  const country = useFreeShippingCountry();
  const { promo } = useShop();
  if (!promo.salePrice && !promo.freeShipping) return null;

  return (
    <div
      className={`overflow-hidden transition-[max-height,opacity] duration-500 ease-out bg-[rgba(11,9,7,0.94)] border-b border-[rgba(208,151,98,0.28)] ${
        collapsed ? 'max-h-0 opacity-0' : 'max-h-14 opacity-100'
      }`}
    >
      {/* One line on wide screens; on narrow phones it wraps into two balanced lines instead of being cut */}
      <p className="promo-in max-w-[146rem] mx-auto px-4 sm:px-8 lg:px-12 min-h-8 py-[7px] flex items-center justify-center text-center text-[12px] sm:text-[12.5px] leading-[1.3] tracking-[0.02em] text-[rgba(242,235,224,0.84)] [text-shadow:none] [text-wrap:balance]">
        <span>
          <span className="font-semibold text-copper">{t('badge')}</span>
          {promo.freeShipping && (
            <>
              {' '}
              <span aria-hidden="true" className="text-[var(--ash)]">
                ·
              </span>{' '}
              {t('freeShippingLimited', { country })}
            </>
          )}
        </span>
      </p>
    </div>
  );
}
